/**
 * 다른 사이트 프로파일링용 브리지.
 *
 * 브라우저에서 다른 origin 의 백엔드를 직접 부르면 CORS 와 세션 쿠키(SameSite=Lax) 때문에 막힌다.
 * 그래서 그 사이트의 프론트 라우트(/profilerBridge)를 작은 팝업으로 열어 두고, postMessage 로
 * 요청을 보내면 그 창이 자기 origin 에서 백엔드를 호출해 결과를 돌려준다.
 * 화면은 로컬에서 보고, 팝업은 요청 대행만 하며 추적종료 시 닫는다.
 *
 * (iframe 은 top-level 이 다른 사이트라 Lax 쿠키가 실리지 않아 쓸 수 없다. 팝업은 top-level 이라 된다.)
 */

export const BRIDGE_PATH = "/profilerBridge";
export const BRIDGE_MSG_TYPE = "snotes-profiler";
const BRIDGE_WINDOW_NAME = "snotes_profiler_bridge";
const READY_TIMEOUT_MS = 15000;
const CALL_TIMEOUT_MS = 20000;

export type BridgeAction =
  | "switch"
  | "getLogs"
  | "getIpLogs"
  | "clearLog"
  | "status";

export interface BridgeRequest {
  type: typeof BRIDGE_MSG_TYPE;
  id: number;
  action: BridgeAction | "close";
  tenantId: string;
  status?: "on" | "off";
}

export interface BridgeResponse {
  type: typeof BRIDGE_MSG_TYPE;
  id: number;
  ok: boolean;
  data?: any;
  error?: string;
}

export interface BridgeReady {
  type: typeof BRIDGE_MSG_TYPE;
  ready: true;
}

interface Pending {
  resolve: (v: any) => void;
  reject: (e: Error) => void;
  timer: number;
}

export class ProfilerBridge {
  private win: Window | null = null;
  private origin = "";
  private seq = 0;
  private pending = new Map<number, Pending>();
  private readyResolve: (() => void) | null = null;

  constructor() {
    window.addEventListener("message", this.onMessage);
  }

  /** 리스너 해제 + 창 닫기 (컴포넌트 unmount 시) */
  dispose() {
    window.removeEventListener("message", this.onMessage);
    this.close();
  }

  isOpen(): boolean {
    return !!this.win && !this.win.closed;
  }

  /** 브리지 창을 열고 ready 신호를 기다린다. 같은 origin 으로 이미 열려 있으면 재사용 */
  async open(baseUrl: string): Promise<void> {
    const origin = new URL(baseUrl).origin;
    if (this.isOpen() && this.origin === origin) return;
    this.close();
    this.origin = origin;

    const url = `${baseUrl}${BRIDGE_PATH}?origin=${encodeURIComponent(
      window.location.origin
    )}`;
    const win = window.open(
      url,
      BRIDGE_WINDOW_NAME,
      "popup=yes,width=420,height=200"
    );
    if (!win) {
      throw new Error("팝업이 차단되었습니다. 이 사이트의 팝업을 허용해 주세요.");
    }
    this.win = win;

    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.readyResolve = null;
        this.close();
        reject(
          new Error(
            `${origin} 연결창이 응답하지 않습니다. 그 사이트에 이 버전이 배포되어 있고 로그인되어 있는지 확인하세요.`
          )
        );
      }, READY_TIMEOUT_MS);
      this.readyResolve = () => {
        window.clearTimeout(timer);
        resolve();
      };
    });
  }

  /** 브리지 창에 요청을 보내고 응답을 기다린다 */
  call<T = any>(
    action: BridgeAction,
    tenantId: string,
    extra: Partial<BridgeRequest> = {}
  ): Promise<T> {
    if (!this.isOpen()) {
      return Promise.reject(new Error("연결창이 닫혀 있습니다."));
    }
    const id = ++this.seq;
    const req: BridgeRequest = {
      type: BRIDGE_MSG_TYPE,
      id,
      action,
      tenantId,
      ...extra,
    };
    return new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error("연결창 응답 시간이 초과되었습니다."));
      }, CALL_TIMEOUT_MS);
      this.pending.set(id, { resolve, reject, timer });
      this.win!.postMessage(req, this.origin);
    });
  }

  /** 창 닫기. 대기 중인 요청은 모두 실패 처리 */
  close() {
    if (this.isOpen()) {
      try {
        const req: BridgeRequest = {
          type: BRIDGE_MSG_TYPE,
          id: 0,
          action: "close",
          tenantId: "",
        };
        this.win!.postMessage(req, this.origin);
        this.win!.close();
      } catch (e) {
        // 이미 닫힌 창
      }
    }
    this.win = null;
    this.pending.forEach((p) => {
      window.clearTimeout(p.timer);
      p.reject(new Error("연결창이 닫혔습니다."));
    });
    this.pending.clear();
  }

  private onMessage = (e: MessageEvent) => {
    if (!this.win || e.source !== this.win || e.origin !== this.origin) return;
    const msg = e.data;
    if (!msg || msg.type !== BRIDGE_MSG_TYPE) return;

    if ((msg as BridgeReady).ready) {
      this.readyResolve?.();
      this.readyResolve = null;
      return;
    }

    const res = msg as BridgeResponse;
    const p = this.pending.get(res.id);
    if (!p) return;
    this.pending.delete(res.id);
    window.clearTimeout(p.timer);
    if (res.ok) p.resolve(res.data);
    else p.reject(new Error(res.error || "연결창 처리 오류"));
  };
}
